// The browse page's filters as a URL: grammar, parser, serializer and the
// filter / sort logic, all pure so the page can derive its state from
// `useSearchParams()` on every render and a search can be shared.
//
//   area       comma list of area slugs (labels accepted too, any case/accents)
//   from, to   yyyy-MM-dd
//   min, max   monthly rent, omitted when at the dynamic bounds (= unbounded)
//   beds 1..5  baths 1..4  type apartment|condo|house|studio|villa
//   amenities, services   comma lists of option labels
//   walk       max minutes on foot to the beach (listings without the fact are out)
//   stay       min months the renter wants to stay (listing minimum must fit)
//   sort       match|price-asc|price-desc
//
// Unknown params are ignored and preserved; `applyBrowseSearch` only ever
// touches these keys, so `?tenant=` (the harness / prerender override) and
// `utm_*` survive every filter change.
import { addDays, differenceInCalendarDays, format, isValid } from "date-fns";

import type { Property } from "@/hooks/useProperties";
import { fromIso, isRangeAvailable, toIso, validateStay } from "@/lib/availability";
import { presentAmenityLabels, resolveAmenityToken } from "@/lib/amenities";
import { foldKey } from "@/lib/foldKey";
import { SERVICE_OPTIONS } from "@/lib/listingOptions";

export const BROWSE_SORTS = ["match", "newest", "price-asc", "price-desc"] as const;
export type BrowseSort = (typeof BROWSE_SORTS)[number];
export const BEDROOM_OPTIONS = ["any", "1", "2", "3", "4", "5"] as const;
export type BedroomOption = (typeof BEDROOM_OPTIONS)[number];
export const BATHROOM_OPTIONS = ["any", "1", "2", "3", "4"] as const;
export type BathroomOption = (typeof BATHROOM_OPTIONS)[number];
export const TYPE_OPTIONS = ["any", "apartment", "condo", "house", "studio", "villa"] as const;
export type TypeOption = (typeof TYPE_OPTIONS)[number];

export const BROWSE_PARAM_KEYS = ["area", "from", "to", "min", "max", "beds", "baths", "type", "amenities", "services", "walk", "stay", "sort"] as const;
export type BrowseParamKey = (typeof BROWSE_PARAM_KEYS)[number];
/** "Clear all" scope: every filter, but not the sort (a view preference). */
export const BROWSE_FILTER_KEYS: readonly BrowseParamKey[] = BROWSE_PARAM_KEYS.filter((k) => k !== "sort");

/** Bounds shown before any listing has loaded. */
export const DEFAULT_PRICE_BOUNDS = { priceMin: 100, priceMax: 10000 };

export interface BrowseContext {
  /** The tenant's area display labels and slugs, same order (tenant.areas / areaSlugs). */
  areaNames: readonly string[];
  areaSlugs: readonly string[];
  /** Dynamic slider bounds; a handle resting on a bound means "unbounded". */
  priceMin: number;
  priceMax: number;
}

export interface BrowseSearch {
  /** Area display labels; a URL token the tenant cannot resolve is kept verbatim. */
  locations: string[];
  fromDate?: Date;
  toDate?: Date;
  /** Effective range, clamped into the context bounds. */
  priceRange: [number, number];
  bedrooms: BedroomOption;
  bathrooms: BathroomOption;
  type: TypeOption;
  selectedAmenities: string[];
  selectedServices: string[];
  /** Max minutes on foot to the beach; undefined = any distance. */
  walkMax?: number;
  /** The months the renter intends to stay; undefined = not stated. */
  stayMonths?: number;
  /** null = not in the URL; the page picks the default (resolveSort). */
  sort: BrowseSort | null;
}

/** Bounds for `walk` and `stay`: one minute to two hours, one month to two years. */
export const WALK_MAX_MINUTES = 120;
export const STAY_MAX_MONTHS = 24;

/** Fold a label or slug to one comparable key: "Mal País" → "mal-pais". */
export const areaKey = foldKey;

/** The slug behind a display label (or a slug-shaped label), else undefined. */
export const areaSlugFor = (label: string, ctx: BrowseContext): string | undefined => {
  const i = ctx.areaNames.indexOf(label);
  if (i >= 0 && ctx.areaSlugs[i]) return ctx.areaSlugs[i];
  const k = areaKey(label);
  return ctx.areaSlugs.find((s) => s === k);
};

/** The display label behind a slug or a label variant, else undefined. */
export const areaLabelFor = (token: string, ctx: BrowseContext): string | undefined => {
  const k = areaKey(token);
  if (!k) return undefined;
  const bySlug = ctx.areaSlugs.findIndex((s) => s === k);
  if (bySlug >= 0 && ctx.areaNames[bySlug]) return ctx.areaNames[bySlug];
  return ctx.areaNames.find((n) => areaKey(n) === k);
};

/** Slider bounds with headroom above the priciest listing (no upper cap). */
export const priceBoundsOf = (properties: ReadonlyArray<{ price: number }>): { priceMin: number; priceMax: number } => {
  const prices = properties.map((p) => p.price).filter((p) => p > 0);
  if (!prices.length) return { ...DEFAULT_PRICE_BOUNDS };
  return {
    priceMin: Math.max(100, Math.floor(Math.min(...prices) / 100) * 100),
    priceMax: Math.max(10000, Math.ceil(Math.max(...prices) / 1000) * 1000),
  };
};

const splitList = (value: string | null): string[] =>
  (value ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const parseDay = (value: string | null): Date | undefined => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const d = fromIso(value);
  return isValid(d) ? d : undefined;
};

const parseAmount = (value: string | null): number | undefined => {
  if (!value || !/^\d+$/.test(value)) return undefined;
  return Number(value);
};

/** A positive whole number capped at `max`; anything else is "not set". */
const parseCount = (value: string | null, max: number): number | undefined => {
  const n = parseAmount(value);
  return n === undefined || n < 1 ? undefined : Math.min(n, max);
};

const oneOf = <T extends string>(options: readonly T[], value: string | null, fallback: T): T =>
  value && (options as readonly string[]).includes(value) ? (value as T) : fallback;

const canonicalOptions = (tokens: string[], options: readonly string[], alias: (t: string) => string): string[] => {
  const out: string[] = [];
  for (const raw of tokens) {
    const wanted = areaKey(alias(raw));
    const match = options.find((o) => areaKey(o) === wanted);
    if (match && !out.includes(match)) out.push(match);
  }
  return out;
};

/** Amenity tokens: labels, keys, synonyms, Spanish, legacy spellings → canonical labels, deduped. */
const canonicalAmenities = (tokens: string[]): string[] => {
  const out: string[] = [];
  for (const raw of tokens) {
    const label = resolveAmenityToken(raw);
    if (label && !out.includes(label)) out.push(label);
  }
  return out;
};

const parseSort = (value: string | null): BrowseSort | null =>
  value && (BROWSE_SORTS as readonly string[]).includes(value) ? (value as BrowseSort) : null;

/** URL → search. Never throws; anything unreadable falls back to "any". */
export function parseBrowseSearch(params: URLSearchParams, ctx: BrowseContext): BrowseSearch {
  const locations: string[] = [];
  const seen = new Set<string>();
  for (const token of splitList(params.get("area"))) {
    const label = areaLabelFor(token, ctx) ?? token;
    const k = areaKey(label);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    locations.push(label);
  }
  const fromDate = parseDay(params.get("from"));
  let toDate = parseDay(params.get("to"));
  if (fromDate && toDate && toDate < fromDate) toDate = undefined;
  const clamp = (n: number) => Math.min(ctx.priceMax, Math.max(ctx.priceMin, n));
  const min = parseAmount(params.get("min"));
  const max = parseAmount(params.get("max"));
  let lo = min === undefined ? ctx.priceMin : clamp(min);
  let hi = max === undefined ? ctx.priceMax : clamp(max);
  if (lo > hi) hi = ctx.priceMax;
  if (lo > hi) lo = ctx.priceMin;
  return {
    locations,
    fromDate,
    toDate,
    priceRange: [lo, hi],
    bedrooms: oneOf(BEDROOM_OPTIONS, params.get("beds"), "any"),
    bathrooms: oneOf(BATHROOM_OPTIONS, params.get("baths"), "any"),
    type: oneOf(TYPE_OPTIONS, params.get("type"), "any"),
    selectedAmenities: canonicalAmenities(splitList(params.get("amenities"))),
    selectedServices: canonicalOptions(splitList(params.get("services")), SERVICE_OPTIONS, (t) => t),
    walkMax: parseCount(params.get("walk"), WALK_MAX_MINUTES),
    stayMonths: parseCount(params.get("stay"), STAY_MAX_MONTHS),
    sort: parseSort(params.get("sort")),
  };
}

/** Search → the browse params only, non-defaults, in a fixed order. */
export function browseSearchToParams(search: BrowseSearch, ctx: BrowseContext): URLSearchParams {
  const p = new URLSearchParams();
  if (search.locations.length) p.set("area", search.locations.map((l) => areaSlugFor(l, ctx) ?? areaKey(l)).filter(Boolean).join(","));
  if (search.fromDate) p.set("from", toIso(search.fromDate));
  if (search.toDate && (!search.fromDate || search.toDate >= search.fromDate)) p.set("to", toIso(search.toDate));
  const [lo, hi] = search.priceRange;
  if (lo > ctx.priceMin) p.set("min", String(Math.round(lo)));
  if (hi < ctx.priceMax && hi >= lo) p.set("max", String(Math.round(hi)));
  if (search.bedrooms !== "any") p.set("beds", search.bedrooms);
  if (search.bathrooms !== "any") p.set("baths", search.bathrooms);
  if (search.type !== "any") p.set("type", search.type);
  if (search.selectedAmenities.length) p.set("amenities", search.selectedAmenities.join(","));
  if (search.selectedServices.length) p.set("services", search.selectedServices.join(","));
  if (search.walkMax) p.set("walk", String(search.walkMax));
  if (search.stayMonths) p.set("stay", String(search.stayMonths));
  if (search.sort) p.set("sort", search.sort);
  return p;
}

/** Replace the browse keys of `prev` with `next`; every other param survives. */
export function applyBrowseSearch(next: URLSearchParams, prev: URLSearchParams): URLSearchParams {
  const p = new URLSearchParams(prev);
  for (const k of BROWSE_PARAM_KEYS) p.delete(k);
  next.forEach((v, k) => p.set(k, v));
  return p;
}

/** Drop every filter, keep the sort and anything that is not a browse key. */
export function clearBrowseFilters(prev: URLSearchParams): URLSearchParams {
  const p = new URLSearchParams(prev);
  for (const k of BROWSE_FILTER_KEYS) p.delete(k);
  return p;
}

/** Only the browse keys, in canonical order, the identity of a search. */
export function pickBrowseParams(params: URLSearchParams): URLSearchParams {
  const p = new URLSearchParams();
  for (const k of BROWSE_PARAM_KEYS) {
    const v = params.get(k);
    if (v) p.set(k, v);
  }
  return p;
}

/**
 * The sort to apply: the URL's when valid, else best match for renters with
 * preferences, else newest first (backlog a0639d4c: "show available houses"
 * opens on the latest additions).
 */
export const resolveSort = (requested: BrowseSort | null, hasPreferences: boolean): BrowseSort => {
  if (requested === "match") return hasPreferences ? "match" : "newest";
  if (requested) return requested;
  return hasPreferences ? "match" : "newest";
};

const SERVICE_KEYS: Record<string, keyof Property["services"]> = {
  Cleaning: "cleaning",
  Electricity: "electricity",
  Gardener: "gardener",
  Internet: "internet",
  "Pool Manager": "poolManager",
  "Property Manager": "propertyManager",
  Water: "water",
};

/** The listings that match a search (the browse page's predicate). */
export function filterProperties(
  properties: readonly Property[],
  search: BrowseSearch,
  ctx: BrowseContext,
  opts: { today?: Date } = {},
): Property[] {
  const wantedAreas = new Set(search.locations.map(areaKey));
  const minBeds = search.bedrooms === "any" ? 0 : Number(search.bedrooms);
  const minBaths = search.bathrooms === "any" ? 0 : Number(search.bathrooms);
  const [lo, hi] = search.priceRange;
  return properties.filter((p) => {
    if (p.price < lo) return false;
    // The upper handle at the dynamic max means "no upper limit".
    if (hi < ctx.priceMax && p.price > hi) return false;
    if (minBeds && p.bedrooms < minBeds) return false;
    if (minBaths && p.bathrooms < minBaths) return false;
    if (wantedAreas.size && !wantedAreas.has(areaKey(p.location))) return false;
    if (search.type !== "any" && p.type !== search.type) return false;
    // Declared features = the array plus what the legacy booleans imply (the
    // same synthesis the match score and the concierge use).
    if (search.selectedAmenities.length) {
      const have = presentAmenityLabels(p);
      for (const a of search.selectedAmenities) if (!have.includes(a)) return false;
    }
    for (const s of search.selectedServices) {
      const key = SERVICE_KEYS[s];
      if (key && !p.services[key]) return false;
    }
    // "Walk to the beach" needs the fact declared: an unknown distance is out.
    if (search.walkMax != null && (p.walkingDistanceToBeachMin == null || p.walkingDistanceToBeachMin > search.walkMax)) return false;
    // A stay shorter than the listing's minimum is out.
    if (search.stayMonths != null && p.minRentalMonths > search.stayMonths) return false;
    // The listing must host the stay: inside the owner's window, no blocked
    // night, at least the minimum stay. With only a move-in date, a
    // minimum-length stay must fit.
    if (search.fromDate) {
      if (search.toDate) {
        if (!isRangeAvailable(p, search.fromDate, addDays(search.toDate, 1), opts.today).ok) return false;
        if (differenceInCalendarDays(search.toDate, search.fromDate) + 1 < p.minRentalMonths * 28) return false;
      } else if (!validateStay(p, search.fromDate, Math.max(p.minRentalMonths, search.stayMonths ?? 0), opts.today).ok) {
        // A move-in with a stay length: the whole stay must fit, not only the minimum.
        return false;
      }
    } else if (search.stayMonths != null && p.availableUntil) {
      // A stay length with no dates: the owner's window must be long enough,
      // starting today or when the home becomes free.
      const today = opts.today ?? new Date();
      const opens = p.availableDate && fromIso(p.availableDate) > today ? fromIso(p.availableDate) : today;
      if (differenceInCalendarDays(addDays(fromIso(p.availableUntil), 1), opens) < search.stayMonths * 28) return false;
    }
    return true;
  });
}

/** A sorted copy; "match" needs a score function, else it falls back to newest. */
export function sortProperties(items: readonly Property[], sort: BrowseSort, score?: (p: Property) => number): Property[] {
  const out = [...items];
  if (sort === "match" && score) return out.sort((a, b) => score(b) - score(a));
  if (sort === "price-asc") return out.sort((a, b) => a.price - b.price);
  if (sort === "price-desc") return out.sort((a, b) => b.price - a.price);
  // Newest first; rows without a date (old prerendered payloads) go last, by price.
  return out.sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? "") || b.price - a.price);
}

/**
 * Paid placement on top of any sort: featured listings first (their order
 * rotates daily so no one owns the top spot), then bumped listings (newest
 * bump first), then everything else in the order it already had.
 */
export function promoteProperties<T extends { id: string }>(
  items: readonly T[],
  promo: { featured: ReadonlySet<string>; bumped: ReadonlyMap<string, number> },
  dayIndex: number,
): T[] {
  if (!promo.featured.size && !promo.bumped.size) return [...items];
  const featured = items.filter((p) => promo.featured.has(p.id));
  const rot = featured.length ? dayIndex % featured.length : 0;
  const bumped = items.filter((p) => !promo.featured.has(p.id) && promo.bumped.has(p.id))
    .sort((a, b) => (promo.bumped.get(b.id) ?? 0) - (promo.bumped.get(a.id) ?? 0));
  const rest = items.filter((p) => !promo.featured.has(p.id) && !promo.bumped.has(p.id));
  return [...featured.slice(rot), ...featured.slice(0, rot), ...bumped, ...rest];
}

export const typeLabel = (t: TypeOption): string => (t === "any" ? "Any type" : t.charAt(0).toUpperCase() + t.slice(1));

/** One active filter as a removable chip; `patch` clears just that filter. */
export interface FilterChip {
  id: string;
  label: string;
  patch: Partial<BrowseSearch>;
}

const shortDay = (d: Date) => format(d, "MMM d");

/**
 * Every active filter as a chip the renter can see and remove (Oct 15: a
 * type set by the profile emptied the page and nothing said so). Panel
 * order; dates are one chip. Applying every patch leaves no filter set.
 */
export function activeFilterChips(search: BrowseSearch, ctx: BrowseContext, money: (n: number) => string): FilterChip[] {
  const out: FilterChip[] = [];
  for (const l of search.locations) out.push({ id: `area:${l}`, label: l, patch: { locations: search.locations.filter((x) => x !== l) } });
  if (search.type !== "any") out.push({ id: "type", label: typeLabel(search.type), patch: { type: "any" } });
  if (search.fromDate || search.toDate) {
    const label = search.fromDate && search.toDate
      ? `${shortDay(search.fromDate)} – ${shortDay(search.toDate)}`
      : search.fromDate ? `From ${shortDay(search.fromDate)}` : `Until ${shortDay(search.toDate as Date)}`;
    out.push({ id: "dates", label, patch: { fromDate: undefined, toDate: undefined } });
  }
  const [lo, hi] = search.priceRange;
  const loSet = lo > ctx.priceMin;
  const hiSet = hi < ctx.priceMax;
  if (loSet || hiSet) {
    const label = loSet && hiSet ? `${money(lo)} – ${money(hi)}` : hiSet ? `Up to ${money(hi)}` : `From ${money(lo)}`;
    out.push({ id: "price", label, patch: { priceRange: [ctx.priceMin, ctx.priceMax] } });
  }
  if (search.bedrooms !== "any") out.push({ id: "beds", label: `${search.bedrooms}+ ${search.bedrooms === "1" ? "bed" : "beds"}`, patch: { bedrooms: "any" } });
  if (search.bathrooms !== "any") out.push({ id: "baths", label: `${search.bathrooms}+ ${search.bathrooms === "1" ? "bath" : "baths"}`, patch: { bathrooms: "any" } });
  for (const a of search.selectedAmenities) out.push({ id: `amenity:${a}`, label: a, patch: { selectedAmenities: search.selectedAmenities.filter((x) => x !== a) } });
  for (const s of search.selectedServices) out.push({ id: `service:${s}`, label: `${s} included`, patch: { selectedServices: search.selectedServices.filter((x) => x !== s) } });
  if (search.walkMax != null) out.push({ id: "walk", label: `${search.walkMax} min walk to the beach`, patch: { walkMax: undefined } });
  if (search.stayMonths != null) out.push({ id: "stay", label: `${search.stayMonths}-month stay`, patch: { stayMonths: undefined } });
  return out;
}

/**
 * For a search with no results: the filters whose removal alone brings homes
 * back, with how many, most first. Empty when nothing single helps.
 */
export function relaxations(
  properties: readonly Property[],
  search: BrowseSearch,
  ctx: BrowseContext,
  money: (n: number) => string,
  opts: { today?: Date } = {},
): Array<{ chip: FilterChip; count: number }> {
  return activeFilterChips(search, ctx, money)
    .map((chip) => ({ chip, count: filterProperties(properties, { ...search, ...chip.patch }, ctx, opts).length }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count);
}

/** How many filters are set, the badge on the mobile Filters button. */
export const countActiveFilters = (search: BrowseSearch, ctx: BrowseContext): number =>
  search.locations.length +
  search.selectedAmenities.length +
  search.selectedServices.length +
  (search.bedrooms !== "any" ? 1 : 0) +
  (search.bathrooms !== "any" ? 1 : 0) +
  (search.type !== "any" ? 1 : 0) +
  (search.fromDate ? 1 : 0) +
  (search.toDate ? 1 : 0) +
  (search.walkMax != null ? 1 : 0) +
  (search.stayMonths != null ? 1 : 0) +
  (search.priceRange[0] > ctx.priceMin || search.priceRange[1] < ctx.priceMax ? 1 : 0);
